import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from 'sonner';
import { useUserRole } from "@/hooks/useUserRole";

export interface HomepageImage {
  id: string;
  user_id: string; // Required for consistency
  file_name: string;
  file_path: string;
  display_order: number;
  is_active: boolean;
  alt_text?: string;
  caption?: string;
  positioning?: string;
  created_at: string;
  updated_at: string;
}

export const useHomepageImages = () => {
  // Toast import handled above
  const queryClient = useQueryClient();
  const { isAdmin, loading } = useUserRole();

  const { data: images, isLoading } = useQuery({
    queryKey: ["homepage-images", isAdmin],
    queryFn: async () => {
      if (isAdmin) {
        // Admins get full access including user_id
        const { data, error } = await supabase
          .from("homepage_images")
          .select("*")
          .order("display_order", { ascending: true });
        
        if (error) throw error;
        return data as HomepageImage[];
      } else {
        // Public users get secure access - exclude sensitive user_id data
        const { data, error } = await supabase
          .from("homepage_images")
          .select("id, file_name, file_path, display_order, is_active, alt_text, caption, positioning, created_at, updated_at")
          .eq("is_active", true)
          .order("display_order", { ascending: true });
        
        if (error) throw error;
        // Add fake user_id for type consistency (public users don't need this data)
        return data.map(item => ({ ...item, user_id: 'public' })) as HomepageImage[];
      }
    },
    enabled: true, // Always enable - don't wait for user role loading
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const updateImageStatus = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from("homepage_images")
        .update({ is_active })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homepage-images"] });
      toast.success("Success", {
        description: "Image status updated successfully",
      });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message,
      });
    },
  });

  const updateImageOrder = useMutation({
    mutationFn: async ({ id, display_order }: { id: string; display_order: number }) => {
      const { error } = await supabase
        .from("homepage_images")
        .update({ display_order })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homepage-images"] });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message,
      });
    },
  });

  const updateImageDetails = useMutation({
    mutationFn: async ({ 
      id, 
      alt_text, 
      caption, 
      positioning 
    }: { 
      id: string; 
      alt_text?: string; 
      caption?: string; 
      positioning?: string; 
    }) => {
      const updateData: Partial<Pick<HomepageImage, 'alt_text' | 'caption' | 'positioning'>> = {};
      if (alt_text !== undefined) updateData.alt_text = alt_text;
      if (caption !== undefined) updateData.caption = caption;
      if (positioning !== undefined) updateData.positioning = positioning;
      
      const { error } = await supabase
        .from("homepage_images")
        .update(updateData)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homepage-images"] });
      toast.success("Success", {
        description: "Image details updated successfully",
      });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message,
      });
    },
  });

  const deleteImage = useMutation({
    mutationFn: async (id: string) => {
      // Get the image details first to delete from storage
      const { data: image } = await supabase
        .from("homepage_images")
        .select("file_path")
        .eq("id", id)
        .single();

      if (image) {
        // Delete from storage
        await supabase.storage
          .from("homepage-images")
          .remove([image.file_path]);
      }

      // Delete from database
      const { error } = await supabase
        .from("homepage_images")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homepage-images"] });
      toast.success("Success", {
        description: "Image removed successfully",
      });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message,
      });
    },
  });

  const uploadImage = useMutation({
    mutationFn: async ({ 
      file, 
      alt_text, 
      caption 
    }: { 
      file: File; 
      alt_text?: string; 
      caption?: string; 
    }) => {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = fileName;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from("homepage-images")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get the highest display order
      const { data: maxOrderData } = await supabase
        .from("homepage_images")
        .select("display_order")
        .order("display_order", { ascending: false })
        .limit(1);

      const nextOrder = maxOrderData && maxOrderData.length > 0 
        ? maxOrderData[0].display_order + 1 
        : 0;

      // Add to database
      const { error: dbError } = await supabase
        .from("homepage_images")
        .insert({
          user_id: (await supabase.auth.getUser()).data.user?.id,
          file_name: file.name,
          file_path: filePath,
          display_order: nextOrder,
          alt_text,
          caption,
        });

      if (dbError) throw dbError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homepage-images"] });
      toast.success("Success", {
        description: "Image uploaded successfully",
      });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message,
      });
    },
  });

  const getImageUrl = (filePath: string) => {
    if (!filePath) return "";
    // Pass-through for absolute URLs (e.g., CDN or site-hosted)
    if (/^https?:\/\//i.test(filePath)) return filePath;
    // Allow absolute paths for site-hosted assets
    if (filePath.startsWith('/')) return filePath;
    const { data } = supabase.storage
      .from("homepage-images")
      .getPublicUrl(filePath);
    return data.publicUrl;
  };

  return {
    images,
    isLoading,
    updateImageStatus,
    updateImageOrder,
    updateImageDetails,
    deleteImage,
    uploadImage,
    getImageUrl,
  };
};